import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Target, TrendingUp, TrendingDown, DollarSign, ChevronLeft, ChevronRight, Settings as SettingsIcon } from "lucide-react";
import { fmtCurrency, fmtCurrencyExact, fmtDate } from "@/lib/constants";

function thisMonth() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

export default function ProfitTracker() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [month, setMonth] = useState(thisMonth());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({ monthly_profit_target: 50000, labour_rate: 85 });

  const { data: summary } = useQuery({ queryKey: ["profit-summary", month], queryFn: async () => (await api.get("/profit/summary", { params: { month } })).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  const saveSettings = useMutation({
    mutationFn: async (body) => (await api.post("/settings", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["profit-summary"] }); setSettingsOpen(false); toast.success("Settings saved"); },
  });

  const monthLabel = new Date(month + "-15").toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  const navMonth = (dir) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const r = summary || { monthly_profit_target: 50000, realized_revenue: 0, realized_cost: 0, realized_profit: 0, realized_count: 0, target_progress_pct: 0, in_progress_revenue: 0, in_progress_cost: 0, in_progress_count: 0, per_job: [] };

  return (
    <div>
      <PageHeader
        title="Profit Tracker"
        subtitle="Monthly P&L roll-up · revenue vs actual cost · variance against monthly target."
        breadcrumb={["System", "Profit"]}
        actions={
          <Button data-testid="profit-settings-btn" variant="outline" onClick={() => { setSettingsDraft(settings || settingsDraft); setSettingsOpen(true); }} className="rounded-sm h-10">
            <SettingsIcon size={14} className="mr-1.5" /> Settings
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-6 space-y-5">
        {/* Month navigator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button data-testid="prev-month-btn" variant="outline" size="sm" onClick={() => navMonth(-1)} className="rounded-sm h-9 w-9 p-0"><ChevronLeft size={16} /></Button>
            <div className="font-display font-semibold text-slate-900 text-xl w-48">{monthLabel}</div>
            <Button data-testid="next-month-btn" variant="outline" size="sm" onClick={() => navMonth(1)} className="rounded-sm h-9 w-9 p-0"><ChevronRight size={16} /></Button>
            <Button variant="ghost" size="sm" onClick={() => setMonth(thisMonth())} className="rounded-sm text-xs">This month</Button>
          </div>
          <div className="text-xs text-slate-500">Labour rate <span className="font-mono font-semibold text-slate-900">{fmtCurrencyExact(r.labour_rate || 85)}/hr</span></div>
        </div>

        {/* Target hero */}
        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
          <div className="bg-slate-900 text-white p-6">
            <div className="flex items-center gap-3 mb-3">
              <Target size={20} className="text-cyan-400" />
              <div className="stamp-label text-cyan-400">Monthly profit target</div>
            </div>
            <div className="flex items-end gap-4 flex-wrap">
              <div>
                <div className="font-display text-5xl font-bold tracking-tight">{fmtCurrency(r.realized_profit)}</div>
                <div className="text-cyan-300 text-sm mt-1">of {fmtCurrency(r.monthly_profit_target)} target</div>
              </div>
              <div className={`font-display text-4xl font-bold ${r.target_progress_pct >= 100 ? "text-emerald-400" : r.target_progress_pct >= 75 ? "text-cyan-400" : "text-amber-400"}`}>
                {r.target_progress_pct}%
              </div>
            </div>
            <div className="mt-5 w-full h-3 bg-slate-700 overflow-hidden">
              <div className={`h-full transition-all ${r.target_progress_pct >= 100 ? "bg-emerald-500" : "bg-cyan-500"}`} style={{ width: `${Math.min(r.target_progress_pct, 100)}%` }} />
            </div>
          </div>
        </Card>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={DollarSign} label="Realized Revenue" value={fmtCurrency(r.realized_revenue)} sub={`${r.realized_count} paid invoices`} accent="text-emerald-600" />
          <Stat icon={TrendingDown} label="Realized Cost" value={fmtCurrency(r.realized_cost)} sub="Labour + materials" accent="text-slate-700" />
          <Stat icon={TrendingUp} label="In-progress Pipeline" value={fmtCurrency(r.in_progress_revenue)} sub={`${r.in_progress_count} jobs`} accent="text-cyan-600" />
          <Stat icon={Target} label="Forecast to Hit Target" value={fmtCurrency(Math.max(r.monthly_profit_target - r.realized_profit, 0))} sub="Profit still needed" accent="text-amber-600" />
        </div>

        {/* Per-job table */}
        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-x-auto">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 stamp-label text-slate-500">All jobs ({r.per_job.length})</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left stamp-label text-slate-500">
                <th className="px-4 py-3">Job</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Revenue</th>
                <th className="px-4 py-3 text-right">Budgeted Cost</th>
                <th className="px-4 py-3 text-right">Actual Cost</th>
                <th className="px-4 py-3 text-right">Variance</th>
                <th className="px-4 py-3 text-right">Profit</th>
                <th className="px-4 py-3 hidden lg:table-cell">Paid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {r.per_job.map((j) => {
                const overBudget = j.variance > 0;
                return (
                  <tr key={j.id} onClick={() => nav(`/jobs/${j.id}`)} className="hover:bg-cyan-50/30 cursor-pointer" data-testid={`profit-row-${j.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{j.title}</div>
                      <div className="font-mono text-[10px] text-slate-400">{j.job_number}</div>
                    </td>
                    <td className="px-4 py-3"><Badge variant="outline" className="rounded-sm text-[10px]">{j.status}</Badge></td>
                    <td className="px-4 py-3 text-right font-mono">{fmtCurrency(j.revenue)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600">{fmtCurrency(j.budgeted_cost)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtCurrency(j.actual_cost)}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      <span className={overBudget ? "text-rose-600 font-semibold" : j.variance < 0 ? "text-emerald-600 font-semibold" : "text-slate-500"}>
                        {j.variance > 0 ? "+" : ""}{fmtCurrency(Math.abs(j.variance))}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{fmtCurrency(j.profit)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">{j.paid_at ? fmtDate(j.paid_at) : "—"}</td>
                  </tr>
                );
              })}
              {r.per_job.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No jobs yet</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Profit settings</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Monthly profit target (AUD)">
              <Input data-testid="target-input" type="number" step="100" value={settingsDraft.monthly_profit_target} onChange={(e) => setSettingsDraft({ ...settingsDraft, monthly_profit_target: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Labour rate (AUD per hour)">
              <Input data-testid="rate-input" type="number" step="0.5" value={settingsDraft.labour_rate} onChange={(e) => setSettingsDraft({ ...settingsDraft, labour_rate: parseFloat(e.target.value) || 0 })} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-settings-btn" onClick={() => saveSettings.mutate(settingsDraft)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent = "text-slate-700" }) {
  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="stamp-label text-slate-500">{label}</div>
          <div className="font-display text-2xl font-semibold mt-2">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 flex items-center justify-center ${accent} bg-slate-50`}><Icon size={17} /></div>
      </div>
    </Card>
  );
}
